import { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { tokenService } from './token-service';

/**
 * Listens for `auth:unauthorized` events dispatched by the API client when a
 * 401 response is received. Attempts a silent token refresh first via
 * tokenService; if that fails (e.g. refresh token missing/expired), falls
 * back to a full logout so the user is redirected to the login page cleanly.
 */
export function useUnauthorizedHandler() {
  const { logout } = useAuth0();
  // Prevent handling multiple 401s simultaneously (e.g. parallel requests)
  const handlingRef = useRef(false);

  useEffect(() => {
    async function handleUnauthorized() {
      if (handlingRef.current) return;
      handlingRef.current = true;

      try {
        // Force a fresh token — bypasses Auth0's cache via the refresh token.
        // On success, Auth0 updates its own cache; the next API call via
        // tokenService.getToken() will automatically pick up the new token.
        const newToken = await tokenService.getToken({ forceRefresh: true });
        if (!newToken) throw new Error('Token refresh returned null');
      } catch {
        // Silent refresh failed — log the user out and let Auth0 redirect to login
        logout({ logoutParams: { returnTo: window.location.origin } });
      } finally {
        // Allow handling again after a short delay to debounce rapid 401s
        setTimeout(() => {
          handlingRef.current = false;
        }, 2000);
      }
    }

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [logout]);
}
